import plantImg from '~/assets/plant/product.png'

import { createEffect, createSignal, Show } from 'solid-js'
import { CheckmarkIcon } from '~/components/icon/checkmark'
import { DonationCards } from '~/components/donation-cards'
import { EnergyParticles } from '~/components/energy-particles'
import { StatsFlow } from '~/components/stats-flow'
import { m } from '~/paraglide/messages'
import { rewardFlow } from '~/store/reward'

export function DonationSection(props: {
  hasDonated: boolean
  userDonatedPlan: number
  refetchUserPurchaseStats: () => void
  refetchPurchaseStats: () => void
}) {
  const [statsFlow, setStatsFlow] = createSignal<number | null>(null)
  const [isFirstDonate, setIsFirstDonate] = createSignal(false)
  const [fireParticles, setFireParticles] = createSignal(false)

  let pendingParticles = false
  let prevRewardItems: unknown = undefined
  createEffect(() => {
    const items = rewardFlow.items
    if (pendingParticles && prevRewardItems != null && items === null) {
      pendingParticles = false
      // Scroll progress bar to show current position before firing particles
      const scrollContainer = document.querySelector<HTMLElement>(
        '[data-progress-scroll]',
      )
      const bar = document.querySelector<HTMLElement>('[data-energy-target]')
      if (scrollContainer && bar) {
        const fill = bar.querySelector<HTMLElement>('[class*="from-"]')
        const fillPct = fill ? parseFloat(fill.style.width) || 25 : 25
        const progressPos = (690 * Math.min(fillPct, 100)) / 100
        const containerWidth = scrollContainer.clientWidth
        const scrollTarget = Math.max(0, progressPos - containerWidth / 2)
        scrollContainer.scrollTo({ left: scrollTarget, behavior: 'smooth' })
      }
      // Fire particles after scroll and dialog exit animation
      setTimeout(() => setFireParticles(true), 800)
      setTimeout(() => setFireParticles(false), 2700)
    }
    prevRewardItems = items
  })

  return (
    <div data-energy-source class="space-y-2">
      <Show
        when={props.hasDonated}
        fallback={
          <DonationCards
            onDonateSuccess={(plan: number) => {
              setIsFirstDonate(true)
              setStatsFlow(plan)
              props.refetchUserPurchaseStats()
              props.refetchPurchaseStats()
            }}
          />
        }
      >
        <div class="flex flex-col">
          <button
            class="group peer relative w-full rounded-xl bg-gradient-to-r from-[#009596] to-[#007F57] p-[2px] transition-transform active:translate-y-[6px] disabled:opacity-50"
            disabled
          >
            <div class="flex h-[46px] items-center justify-center space-x-[6px] rounded-xl bg-gradient-to-r from-[#2AF4FF] to-[#18FE95]">
              <span class="text-[18px] font-bold text-[#013E45]">
                {m.donation_complete()}
              </span>
              <CheckmarkIcon />
            </div>
            <div class="h-[6px] max-h-[6px] transition-all group-active:max-h-0" />
          </button>
          <div class="h-[6px] max-h-0 transition-all peer-active:max-h-[6px]" />

          <button
            class="mx-auto mt-4 font-semibold text-white underline"
            onClick={() => {
              setStatsFlow(props.userDonatedPlan)
            }}
          >
            {m.recap_my_donation()}
          </button>
        </div>
      </Show>

      <StatsFlow
        isOpen={statsFlow() !== null}
        close={() => {
          setStatsFlow(null)
          if (isFirstDonate()) {
            setIsFirstDonate(false)
            pendingParticles = true
            rewardFlow.start([
              {
                icon: plantImg,
                type: 'tree',
                gid: 163,
                title: m.tree_type_163_title(),
              },
            ])
          }
        }}
        plan={statsFlow() ?? props.userDonatedPlan}
      />

      <EnergyParticles fire={fireParticles} />
    </div>
  )
}
